import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { STATUS_LABEL, type ProjectSummary, type Template } from '../lib/types';
import { Spinner, useToast } from '../components/ui';

function ago(iso: string): string {
  const mins = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

export function ProjectsPage() {
  const { can } = useAuth();
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [sample, setSample] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [menu, setMenu] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const toast = useToast();

  const load = () => api.listProjects().then(setProjects).catch((e) => toast.push(e.message, true));

  useEffect(() => {
    void load();
    api.listTemplates().then(setTemplates).catch(() => {});
  }, []);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menu]);

  const create = async () => {
    if (!name.trim()) return;
    try {
      const project = await api.createProject({
        name: name.trim(),
        ...(sample ? { seed: 'garmisch' as const } : {}),
        ...(templateId ? { templateId } : {}),
      });
      navigate(`/projects/${project.id}/${sample ? 'preview' : 'import'}`);
    } catch (e) {
      toast.push((e as Error).message, true);
    }
  };

  const duplicate = async (p: ProjectSummary) => {
    const copyName = window.prompt('Name for the copy', `${p.name} (copy)`);
    if (!copyName) return;
    try {
      const copy = await api.duplicateProject(p.id, copyName);
      toast.push(`Copied to "${copy.name}" — photos included`);
      navigate(`/projects/${copy.id}/content`);
    } catch (e) {
      toast.push((e as Error).message, true);
    }
  };

  const remove = async (p: ProjectSummary) => {
    if (!window.confirm(`Delete "${p.name}"? Its photos, versions and PDFs are deleted too.`)) return;
    try {
      await api.deleteProject(p.id);
      toast.push('Booklet deleted');
      await load();
    } catch (e) {
      toast.push((e as Error).message, true);
    }
  };

  if (!projects) return <div className="row"><Spinner /> <span className="sub">Loading…</span></div>;

  const q = query.trim().toLowerCase();
  const shown = projects.filter((p) => !q || p.name.toLowerCase().includes(q) || p.title.toLowerCase().includes(q));

  return (
    <>
      <div className="row" style={{ marginBottom: 22 }}>
        <div>
          <h1 className="title">Your booklets</h1>
          <div className="sub">{projects.length ? `${projects.length} booklet${projects.length > 1 ? 's' : ''}` : 'Nothing here yet'}</div>
        </div>
        <div style={{ flex: 1 }} />
        {projects.length > 6 && <input className="search" placeholder="Search booklets…" value={query} onChange={(e) => setQuery(e.target.value)} />}
        {can('edit') && <button className="btn primary" onClick={() => setCreating((v) => !v)}>+ New booklet</button>}
      </div>

      {creating && (
        <div className="panel" style={{ maxWidth: 560 }}>
          <div className="field">
            <label>Booklet name</label>
            <input
              value={name}
              autoFocus
              placeholder="e.g. Garmisch Motorbike Adventure 2027"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void create()}
            />
          </div>
          {templates.length > 1 && (
            <div className="field">
              <label>Template</label>
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                {templates.map((t) => <option key={t.id} value={t.builtIn ? '' : t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          <label className="row sub" style={{ gap: 8, marginBottom: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={sample} onChange={(e) => setSample(e.target.checked)} />
            Fill with the Garmisch sample so I can see the layout
          </label>
          <div className="sub" style={{ fontSize: 12, marginBottom: 12 }}>
            Updating last year’s booklet? Use <b>Copy</b> on its card instead — text, prices and photos come along.
          </div>
          <div className="row end">
            <button className="btn ghost" onClick={() => setCreating(false)}>Cancel</button>
            <button className="btn primary" disabled={!name.trim()} onClick={() => void create()}>Create</button>
          </div>
        </div>
      )}

      {!projects.length && !creating && (
        <div className="empty-state">
          <div className="big">No booklets yet</div>
          <div className="sub">Create one, then drop in the DOCX and the price sheet — the booklet builds itself.</div>
          {can('edit') && <button className="btn primary" style={{ marginTop: 14 }} onClick={() => setCreating(true)}>Create your first booklet</button>}
        </div>
      )}

      <div className="gallery">
        {shown.map((p) => (
          <div className="book-card" key={p.id} onClick={() => navigate(`/projects/${p.id}/preview`)}>
            <div className="thumb">
              <img src={`/api/projects/${p.id}/thumbnail.png?v=${encodeURIComponent(p.updatedAt)}`} alt="" loading="lazy" />
            </div>
            <div className="body">
              <div className="row" style={{ gap: 6, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                <div className="name" style={{ flex: 1 }}>{p.name}</div>
                {can('edit') && (
                  <div className="menu-wrap" onClick={(e) => e.stopPropagation()}>
                    <button className="btn small ghost kebab" aria-label="More" onClick={() => setMenu(menu === p.id ? null : p.id)}>⋯</button>
                    {menu === p.id && (
                      <div className="menu">
                        <button onClick={() => { setMenu(null); void duplicate(p); }}>Copy booklet</button>
                        <button onClick={() => { setMenu(null); navigate(`/projects/${p.id}/history`); }}>History</button>
                        <button className="danger" onClick={() => { setMenu(null); void remove(p); }}>Delete</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="sub">
                {p.durationDays ? `${p.durationDays} days` : 'No days yet'} · {ago(p.updatedAt)}
              </div>
              <div className="row" style={{ gap: 6, marginTop: 8 }}>
                <span className={`state ${p.status}`}>
                  {p.blockers > 0 && p.status !== 'PUBLISHED'
                    ? `${p.blockers} thing${p.blockers > 1 ? 's' : ''} to finish`
                    : STATUS_LABEL[p.status]}
                </span>
                {p.openComments > 0 && <span className="state comments">💬 {p.openComments}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
